import React, { useState, useEffect } from "react";
import { Mail, Phone, MapPin, Send, CheckCircle2, Headphones, MessageSquare, Briefcase } from "lucide-react";
import { toast } from "sonner";
import MarketingShell from "../components/marketing/MarketingShell";
import { api, errMsg } from "../lib/api";

const CHANNELS = [
  { icon: Headphones,    title: "Sales",    desc: "Demos, pricing, custom deals", email: "info@ecomcatalog.ai" },
  { icon: MessageSquare, title: "Support",   desc: "Account & product help",       email: "business@ecomcatalog.ai" },
  { icon: Briefcase,     title: "Partnerships", desc: "Agencies, integrations, press", email: "business@ecomcatalog.ai" },
];

export default function Contact() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [company, setCompany] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    document.title = "Contact — EcomCatalog AI · Talk to Sales, Support or Partners";
  }, []);

  async function submit(e) {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      toast.error("Please share your name and email");
      return;
    }
    setBusy(true);
    try {
      await api.post("/public/demo-request", { name, email, phone, company, message });
      setSent(true);
      toast.success("Got it — we'll reach out within 24 hours.");
    } catch (e) {
      toast.error(errMsg(e, "Couldn't submit. Please email info@ecomcatalog.ai"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <MarketingShell>
      <section className="pt-16 sm:pt-24 pb-12">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            {/* Left — info */}
            <div className="lg:col-span-5">
              <div className="text-[11px] uppercase tracking-[0.3em] text-indigo-400 font-bold">
                Contact
              </div>
              <h1 className="font-mkt-display text-5xl sm:text-6xl font-semibold tracking-tighter mt-4 leading-[1.02]">
                Let's <span className="mkt-gradient-text-vivid">talk.</span>
              </h1>
              <p className="text-lg text-slate-400 mt-5 leading-relaxed max-w-md">
                15-min walkthroughs · custom integration scoping · agency partnerships.
                Drop a line — we'll reply within 24 hours.
              </p>

              <div className="mt-10 space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-xl bg-white/[0.04] border border-white/[0.08] grid place-items-center shrink-0">
                    <Mail size={14} className="text-indigo-300" />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">Email</div>
                    <a href="mailto:info@ecomcatalog.ai" className="text-sm text-slate-200 hover:text-white">
                      info@ecomcatalog.ai
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-xl bg-white/[0.04] border border-white/[0.08] grid place-items-center shrink-0">
                    <Phone size={14} className="text-indigo-300" />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">Phone</div>
                    <a href="tel:+919464645491" className="text-sm text-slate-200 hover:text-white">
                      +91 94646 45491
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-xl bg-white/[0.04] border border-white/[0.08] grid place-items-center shrink-0">
                    <MapPin size={14} className="text-indigo-300" />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">Location</div>
                    <div className="text-sm text-slate-200">India · Remote-first</div>
                  </div>
                </div>
              </div>

              <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-3">
                {CHANNELS.map((c) => (
                  <a
                    key={c.title}
                    href={`mailto:${c.email}`}
                    className="mkt-glass mkt-hover-lift p-5 block"
                    data-testid={`contact-${c.title.toLowerCase()}`}
                  >
                    <c.icon size={16} className="text-indigo-300" />
                    <div className="font-mkt-display text-base font-semibold mt-3">{c.title}</div>
                    <div className="text-xs text-slate-400 mt-1">{c.desc}</div>
                  </a>
                ))}
              </div>
            </div>

            {/* Right — form */}
            <div className="lg:col-span-7">
              <div className="mkt-glass-strong p-8 sm:p-10" data-testid="contact-form-card">
                {sent ? (
                  <div className="text-center py-16">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 grid place-items-center mx-auto mb-5">
                      <CheckCircle2 size={28} className="text-white" />
                    </div>
                    <h2 className="font-mkt-display text-3xl font-semibold tracking-tight">
                      Message received.
                    </h2>
                    <p className="text-slate-400 mt-3 max-w-sm mx-auto">
                      We'll reach out within 24 hours on <b className="text-white">{email}</b>.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-indigo-300 font-bold">
                      Send us a message
                    </div>
                    <h2 className="font-mkt-display text-3xl font-semibold tracking-tight">
                      Tell us a bit about your brand.
                    </h2>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-4">
                      <div>
                        <label className="text-xs text-slate-400 mb-1.5 block">Name *</label>
                        <input
                          value={name} onChange={(e) => setName(e.target.value)}
                          className="w-full px-3.5 py-2.5 bg-white/[0.03] border border-white/[0.1] rounded-xl text-white text-sm focus:outline-none focus:border-indigo-400/60 transition"
                          data-testid="contact-name"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-slate-400 mb-1.5 block">Email *</label>
                        <input
                          type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                          className="w-full px-3.5 py-2.5 bg-white/[0.03] border border-white/[0.1] rounded-xl text-white text-sm focus:outline-none focus:border-indigo-400/60 transition"
                          data-testid="contact-email"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-slate-400 mb-1.5 block">Phone</label>
                        <input
                          value={phone} onChange={(e) => setPhone(e.target.value)}
                          className="w-full px-3.5 py-2.5 bg-white/[0.03] border border-white/[0.1] rounded-xl text-white text-sm focus:outline-none focus:border-indigo-400/60 transition"
                          data-testid="contact-phone"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-slate-400 mb-1.5 block">Brand / Company</label>
                        <input
                          value={company} onChange={(e) => setCompany(e.target.value)}
                          className="w-full px-3.5 py-2.5 bg-white/[0.03] border border-white/[0.1] rounded-xl text-white text-sm focus:outline-none focus:border-indigo-400/60 transition"
                          data-testid="contact-company"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-slate-400 mb-1.5 block">Message</label>
                      <textarea
                        rows={5} value={message} onChange={(e) => setMessage(e.target.value)}
                        placeholder="Tell us about your marketplaces, SKU count, and what you'd like to automate..."
                        className="w-full px-3.5 py-2.5 bg-white/[0.03] border border-white/[0.1] rounded-xl text-white text-sm focus:outline-none focus:border-indigo-400/60 transition"
                        data-testid="contact-message"
                      />
                    </div>
                    <button
                      type="submit" disabled={busy}
                      className="mkt-btn-gradient w-full justify-center disabled:opacity-60"
                      data-testid="contact-submit"
                    >
                      {busy ? "Sending…" : (<>Send message <Send size={14} /></>)}
                    </button>
                    <p className="text-[11px] text-slate-500 text-center">
                      By submitting you agree to our terms. We never share your email.
                    </p>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </MarketingShell>
  );
}
